import AsyncStorage from '@react-native-async-storage/async-storage';

const UPLOAD_HISTORY_KEY = 'upload_history';
const MAX_UPLOADS_PER_HOUR = 10;
const ONE_HOUR_MS = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Rate limiting service for image uploads
 */
class RateLimitService {
  /**
   * Check if user can upload more images
   * @returns True if under limit, false if exceeded
   */
  async checkUploadLimit(): Promise<boolean> {
    try {
      const historyJson = await AsyncStorage.getItem(UPLOAD_HISTORY_KEY);
      const history: number[] = historyJson ? JSON.parse(historyJson) : [];

      // Filter out timestamps older than 1 hour
      const now = Date.now();
      const recentUploads = history.filter(
        (timestamp) => now - timestamp < ONE_HOUR_MS
      );

      // Check if under limit
      return recentUploads.length < MAX_UPLOADS_PER_HOUR;
    } catch (error) {
      console.error('Error checking upload limit:', error);
      // Fail open - allow upload if we can't check
      return true;
    }
  }

  /**
   * Record a new upload
   */
  async recordUpload(): Promise<void> {
    try {
      const historyJson = await AsyncStorage.getItem(UPLOAD_HISTORY_KEY);
      const history: number[] = historyJson ? JSON.parse(historyJson) : [];

      // Add current timestamp
      const now = Date.now();
      history.push(now);

      // Clean up old timestamps (older than 1 hour)
      const cleanedHistory = history.filter(
        (timestamp) => now - timestamp < ONE_HOUR_MS
      );

      // Save back to storage
      await AsyncStorage.setItem(
        UPLOAD_HISTORY_KEY,
        JSON.stringify(cleanedHistory)
      );
    } catch (error) {
      console.error('Error recording upload:', error);
      // Continue even if recording fails
    }
  }

  /**
   * Get remaining upload count for current hour
   * @returns Number of uploads remaining
   */
  async getRemainingUploads(): Promise<number> {
    try {
      const historyJson = await AsyncStorage.getItem(UPLOAD_HISTORY_KEY);
      const history: number[] = historyJson ? JSON.parse(historyJson) : [];

      // Filter out timestamps older than 1 hour
      const now = Date.now();
      const recentUploads = history.filter(
        (timestamp) => now - timestamp < ONE_HOUR_MS
      );

      return Math.max(0, MAX_UPLOADS_PER_HOUR - recentUploads.length);
    } catch (error) {
      console.error('Error getting remaining uploads:', error);
      return MAX_UPLOADS_PER_HOUR;
    }
  }

  /**
   * Clear upload history (for testing/debugging)
   */
  async clearHistory(): Promise<void> {
    try {
      await AsyncStorage.removeItem(UPLOAD_HISTORY_KEY);
    } catch (error) {
      console.error('Error clearing upload history:', error);
    }
  }
}

// Export singleton instance
export const rateLimitService = new RateLimitService();
